import { ReactNode, useState, useCallback, useEffect } from 'react';
import { cn } from '@/utils/cn';

const COLLAPSED_KEY = 'post-creator-collapsed-sections';

/** Read collapsed set from localStorage */
function getCollapsedSections(): Set<string> {
  try {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    if (stored) return new Set(JSON.parse(stored) as string[]);
  } catch { /* ignore */ }
  return new Set();
}

/** Persist collapsed set to localStorage */
function saveCollapsedSections(set: Set<string>) {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

interface CreatorSectionCardProps {
  id: string;
  title: string;
  /** Material Symbols icon name shown before the title */
  icon?: string;
  children: ReactNode;
  className?: string;
}

export const CreatorSectionCard = ({ id, title, icon, children, className }: CreatorSectionCardProps) => {
  const [collapsed, setCollapsed] = useState(() => getCollapsedSections().has(id));
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const set = getCollapsedSections();
    if (collapsed) {
      set.add(id);
    } else {
      set.delete(id);
    }
    saveCollapsedSections(set);
  }, [collapsed, id]);

  const toggleCollapse = useCallback(() => {
    setAnimating(true);
    setCollapsed((prev) => !prev);
  }, []);

  return (
    <div
      id={id}
      className={cn(
        'mt-4 first:mt-0 bg-off-white rounded-xl border border-gray-200 animate-slide-in-from-top transition-all duration-200 ease-in-out',
        'px-4 py-3',
        className
      )}
    >
      {/* Section header */}
      <button
        type="button"
        onClick={toggleCollapse}
        className="flex items-center gap-2 w-full text-left group"
      >
        {icon && (
          <span
            className="material-symbols-outlined text-gray-500 group-hover:text-gray-700 transition-colors"
            style={{ fontSize: 'clamp(13px, 1.5vh, 17px)' }}
          >
            {icon}
          </span>
        )}
        <span className="text-xs sm:text-sm font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">
          {title}
        </span>
        <span
          className="material-symbols-outlined text-gray-400 group-hover:text-gray-600 ml-auto transition-all duration-200"
          style={{ fontSize: 'clamp(13px, 1.5vh, 17px)', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
        >
          expand_more
        </span>
      </button>

      {/* Content with animated grid rows */}
      <div
        className={cn('grid transition-[grid-template-rows] duration-200 ease-in-out', !collapsed && 'mt-3')}
        style={{ gridTemplateRows: collapsed ? '0fr' : '1fr' }}
        onTransitionEnd={() => setAnimating(false)}
      >
        <div className={collapsed || animating ? 'overflow-hidden' : 'overflow-visible'}>
          {children}
        </div>
      </div>
    </div>
  );
};
